import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";

const AssignTaskModal = ({
  show,
  onClose,
  projectId,
  onTaskCreated,
  editingTask = null,
  onTaskUpdated,
  onTaskDeleted,
}) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    user_id: "",
    title: "",
    description: "",
    priority: "Medium",
    due_date: "",
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (show) {
      fetchMembers();
      if (editingTask) {
        setForm({
          user_id: editingTask.user_id,
          title: editingTask.title,
          description: editingTask.description || "",
          priority: editingTask.priority,
          due_date: editingTask.due_date || "",
        });
      } else {
        setForm({
          user_id: "",
          title: "",
          description: "",
          priority: "Medium",
          due_date: "",
        });
      }
    }
  }, [show, editingTask]);

  const fetchMembers = async () => {
    setLoading(true);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(
        `https://loopsneller.com/api/projects/${projectId}/members`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json();
      setMembers(data);
    } catch (err) {
      console.error("Error fetching members", err);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    const token = localStorage.getItem("auth_token");

    const url = editingTask
      ? `https://loopsneller.com/api/tasks/${editingTask.id}`
      : "https://loopsneller.com/api/tasks";
    const method = editingTask ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...form, project_id: projectId }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Task request failed");

      setSuccess(
        editingTask
          ? "Task updated successfully!"
          : "Task assigned successfully!"
      );
      if (editingTask) {
        onTaskUpdated(data.task || data);
      } else {
        onTaskCreated?.(); // ⬅️ trigger refresh or custom logic
      }
      onClose();
    } catch (err) {
      setError(err.message || "Failed to assign/update task");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const token = localStorage.getItem("auth_token");
    if (!window.confirm("Are you sure you want to delete this task?")) return;

    try {
      const res = await fetch(
        `https://loopsneller.com/api/tasks/${editingTask.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to delete task");

      setSuccess("Task deleted successfully!");
      onTaskDeleted(editingTask.id);
      onClose();
    } catch (err) {
      setError(err.message || "Error deleting task");
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{editingTask ? "Edit Task" : "Assign Task"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleTaskSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Assign To</Form.Label>
            <Form.Select
              value={form.user_id}
              onChange={(e) => setForm({ ...form, user_id: e.target.value })}
              required
            >
              <option value="">Select member</option>
              {members.map((m) => (
                <option key={m.user.id} value={m.user.id}>
                  {m.user.name} ({m.role})
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Task Title</Form.Label>
            <Form.Control
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Priority</Form.Label>
            <Form.Select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              required
            >
              <option>Low</option>
              <option>Medium</option>
              <option>High</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Due Date</Form.Label>
            <Form.Control
              type="date"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            />
          </Form.Group>

          <div className="d-flex justify-content-between">
            <Button type="submit" disabled={loading}>
              {loading ? (
                <Spinner size="sm" />
              ) : editingTask ? (
                "Update Task"
              ) : (
                "Assign Task"
              )}
            </Button>
            {editingTask && (
              <Button
                variant="danger"
                onClick={handleDelete}
                disabled={loading}
              >
                Delete
              </Button>
            )}
          </div>
        </Form>

        {success && (
          <Alert variant="success" className="mt-3">
            {success}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default AssignTaskModal;
